import { listForm } from '@orientaction/Context';
import { pluralizeAdminTabLabel, valueAliasRole } from '@orientaction/utils';
import { CONSULTANT_ROLE_ID, DIRECTION_ROLE_ID } from '../shared/constants';

export const CONSULTANT_PROFILE_TABS = [
  { id: 'profil', label: 'Profil' },
  { id: 'prestations', label: 'Prestations' },
  { id: 'tests-mineurs', label: 'Tests mineurs autorisés' },
  { id: 'beneficiaires', label: 'Bénéficiaires rattachés' },
] as const;

export type ConsultantProfileTabId =
  | 'profil'
  | 'prestations'
  | 'tests-mineurs'
  | 'beneficiaires'
  | 'manager_3'
  | 'manager_2'
  | 'manager_1';

export type HierarchyAttachedTab = {
  id: ConsultantProfileTabId;
  hierarchyKey: string;
  label: string;
  roleId: number;
};

export const getHierarchyAttachedTabs = (
  roleId?: number,
  companyUserRoleAlias?: any
): HierarchyAttachedTab[] => {
  if (!roleId || roleId === CONSULTANT_ROLE_ID || roleId >= 5) {
    return [];
  }

  return listForm.slice(Math.max(0, roleId - 1)).map(item => {
    const isBeneficiaries = item.name === 'Bénéficiaires';
    return {
      id: (isBeneficiaries ? 'beneficiaires' : item.name) as ConsultantProfileTabId,
      hierarchyKey: item.name,
      label: pluralizeAdminTabLabel(
        isBeneficiaries
          ? 'Bénéficiaire'
          : valueAliasRole(item.name, companyUserRoleAlias) || item.label
      ),
      roleId: item.userTypeConnected,
    };
  });
};

export const getProfileTabs = (roleId?: number, companyUserRoleAlias?: any) => {
  if (roleId === CONSULTANT_ROLE_ID) {
    return CONSULTANT_PROFILE_TABS.map(item => ({ id: item.id as ConsultantProfileTabId, label: item.label }));
  }
  return [
    { id: 'profil' as ConsultantProfileTabId, label: 'Profil' },
    ...getHierarchyAttachedTabs(roleId, companyUserRoleAlias),
  ];
};

export const BENEFICIARIES_PAGE_SIZE = 10;

export const MAX_VISIBLE_PRESTATIONS = 3;

export const EDIT_PROFILE_TITLE = 'Modifier le profil du consultant';

export const PROFILE_UPDATED_MESSAGE = 'Le profil a été mis à jour avec succès';

export const CATEGORIES_UPDATED_MESSAGE = 'Les tests mineurs autorisés ont été mis à jour';

export const GENERATED_PASSWORD_TITLE = 'Générer un mot de passe aléatoirement';

export const GENERATED_PASSWORD_TEXT =
  "Transmettez ce mot de passe temporaire au consultant afin qu'il puisse définir un nouveau mot de passe.";

export const STATUS_ACTIVE = 'Actif';
export const STATUS_INACTIVE = 'Inactif';

export const DISABLE_USER_TITLE = "Êtes-vous sûr de vouloir désactiver l'utilisateur ?";
export const DISABLE_USER_TEXT =
  "Vous êtes sur le point de désactiver l'utilisateur. Le statut de l'utilisateur passera de “Actif” à “Inactif”.";
export const DISABLE_USER_CONFIRM = 'Désactiver';
export const DISABLE_USER_SUCCESS = "L'utilisateur a bien été désactivé.";

export const ENABLE_USER_TITLE = "Êtes-vous sûr de vouloir réactiver l'utilisateur ?";
export const ENABLE_USER_TEXT =
  "Vous êtes sur le point de réactiver l'utilisateur. Le statut de l'utilisateur passera de “Inactif” à “Actif”.";
export const ENABLE_USER_CONFIRM = 'Réactiver';
export const ENABLE_USER_SUCCESS = "L'utilisateur a bien été réactivé.";

export const DELETE_USER_TITLE = 'Suppression définitive';
export const DELETE_USER_TEXT =
  'Êtes-vous sûr de vouloir supprimer ce compte ? Cette action est définitive.';
export const DELETE_USER_CONFIRM = 'Confirmer';
export const DELETE_USER_SUCCESS = 'Le compte a bien été supprimé.';

export const DELETE_PRESTATION_TITLE = 'Supprimer la prestation';
export const DELETE_PRESTATION_CONFIRM = 'Supprimer';
export const DELETE_PRESTATION_SUCCESS = 'La prestation a bien été retirée du consultant.';

export const ADD_PRESTATION_TITLE = 'Ajouter une prestation';
export const ADD_PRESTATION_LABEL = 'Prestation';
export const ADD_PRESTATION_EMPTY = 'Aucune prestation disponible';
export const ADD_PRESTATION_SUCCESS = 'La prestation a bien été ajoutée au consultant.';

export const buildDeletePrestationText = (prestationName: string) =>
  `Le consultant n’aura plus la prestation « ${prestationName} » dans ses habilitations. Les bénéficiaires déjà rattachés à cette prestation ne seront pas impactés.`;

export const REASSIGN_BENEFICIARIES_TITLE = 'Ré-assigner les bénéficiaires';
export const REASSIGN_BENEFICIARIES_LABEL = 'Nouveau consultant';
export const REASSIGN_BENEFICIARIES_HINT =
  'Seuls les consultants habilités à toutes les prestations des bénéficiaires rattachés sont proposés.';
export const REASSIGN_BENEFICIARIES_EMPTY =
  "Aucun consultant n'a toutes les prestations nécessaires.";
export const REASSIGN_BENEFICIARIES_SUCCESS = 'Les bénéficiaires ont été réassignés.';
export const REASSIGN_BENEFICIARIES_MISMATCH =
  'Vous ne pouvez pas réassigner tous les comptes de ce consultant : les prestations du nouveau consultant ne sont pas les mêmes';

export const getDirectReassignRoleId = (roleId?: number) => {
  if (!roleId || roleId >= CONSULTANT_ROLE_ID) {
    return null;
  }
  return roleId + 1;
};

export const isDirectReassignTab = (currentRoleId?: number, attachedRoleId?: number) =>
  Boolean(
    currentRoleId && attachedRoleId && attachedRoleId === getDirectReassignRoleId(currentRoleId)
  );

export const buildReassignHierarchyTitle = (attachedLabel: string) =>
  `Ré-assigner les ${attachedLabel}`;

export const buildReassignHierarchyLabel = (roleLabel: string) =>
  `Nouveau ${roleLabel || 'utilisateur'}`;

export const REASSIGN_HIERARCHY_EMPTY = 'Aucun utilisateur disponible';
export const REASSIGN_HIERARCHY_SUCCESS = 'Les comptes ont été réassignés.';
export const REASSIGN_HIERARCHY_MISMATCH =
  'Vous ne pouvez pas réassigner tous les comptes de cet utilisateur : les prestations du nouvel utilisateur ne sont pas les mêmes';

export const getBackLabel = (roleId?: number) => {
  if (roleId === CONSULTANT_ROLE_ID) {
    return '← Retour aux consultants';
  }
  if (roleId === DIRECTION_ROLE_ID) {
    return '← Retour à la direction';
  }
  return '← Retour à la liste';
};
